/**
 * Location inventory (variant metafields):
 * - Reads custom.inventory_windsor / inventory_halifax / inventory_mississauga
 * - Sourced from .location-inventory-map JSON and data-inventory-* attributes
 * - Checkout pickup UI is disabled separately (Maximize.pickupEnabled)
 * - Gate ATC / validate cart qty against selected store
 */
if (!window.Maximize?.loadedScript?.includes("location-inventory.js")) {
  if (window.Maximize?.loadedScript) {
    window.Maximize.loadedScript.push("location-inventory.js");
  }

  (function () {
    const MARKET_KEYS = ["windsor", "halifax", "mississauga"];
    const EVENT_STORE_CHANGED = "maximize:store-changed";
    const EVENT_CART_UPDATED = "maximize:cart:updated";
    const EVENT_INVENTORY_CHECKED = "maximize:location-inventory:checked";

    const mapCache = new WeakMap();
    /** @type {Map<string, Promise<object|null>>} */
    const variantFetchCache = new Map();
    let cartCheckPromise = null;
    let selfUpdatingCart = false;

    function normalizeName(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/&amp;/g, "&")
        .replace(/&#39;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    }

    function marketKey(name) {
      const n = normalizeName(name);
      if (!n) return null;
      for (const key of MARKET_KEYS) {
        if (n.includes(key)) return key;
      }
      return null;
    }

    function getSelectedStore() {
      try {
        if (window.Alpine && Alpine.store("xStoreMap")?.currentStoreSelected) {
          return String(Alpine.store("xStoreMap").currentStoreSelected).trim();
        }
      } catch (_) {}
      try {
        const saved = JSON.parse(localStorage.getItem("currentStoreSelected") || "null");
        return String(saved?.selectedStore || "").trim();
      } catch (_) {
        return "";
      }
    }

    const LOCATION_ATTRS = [
      ["windsor", "Windsor Location"],
      ["halifax", "Halifax Location"],
      ["mississauga", "Mississauga Location"],
    ];

    function locationsFromDataElement(el) {
      if (!el?.getAttribute) return null;
      const locations = {};
      let found = false;
      for (const [key, locName] of LOCATION_ATTRS) {
        const raw = el.getAttribute(`data-inventory-${key}`);
        if (raw === null || raw === "") continue;
        found = true;
        const qty = Number(raw);
        const quantityAvailable = Number.isFinite(qty) ? qty : 0;
        locations[locName] = {
          available: quantityAvailable > 0,
          quantityAvailable,
          source: "metafield",
        };
      }
      return found ? locations : null;
    }

    function collectMaps() {
      return [...document.querySelectorAll(".location-inventory-map")];
    }

    function inventoryFromMaps(variantId, contextEl) {
      const id = String(variantId);
      const preferred = [];
      if (contextEl) {
        const local = findMapElement(contextEl);
        if (local) preferred.push(local);
      }
      const cartMap = document.querySelector(".location-inventory-map[data-cart-inventory-map]");
      if (cartMap) preferred.push(cartMap);
      const ordered = [...preferred, ...collectMaps()];
      const seen = new Set();
      for (const el of ordered) {
        if (!el || seen.has(el)) continue;
        seen.add(el);
        const map = readMapFromElement(el);
        const row = getVariantAvailabilityFromMap(map, id);
        if (row && typeof row === "object" && Object.keys(row).length) {
          return row;
        }
      }
      return null;
    }

    function inventoryFromDataAttrs(variantId, contextEl) {
      const id = String(variantId);
      const candidates = [];
      if (contextEl?.closest) {
        const near =
          contextEl.closest(`[data-variant-id="${id}"]`) ||
          contextEl.closest("[data-variant-id]") ||
          contextEl;
        candidates.push(near);
      }
      candidates.push(
        ...document.querySelectorAll(
          `[data-variant-id="${id}"][data-inventory-windsor], [data-variant-id="${id}"][data-inventory-halifax], [data-variant-id="${id}"][data-inventory-mississauga]`
        )
      );
      for (const el of candidates) {
        const locs = locationsFromDataElement(el);
        if (locs) return locs;
      }
      return null;
    }

    function toVariantGid(variantId) {
      const id = String(variantId || "");
      if (id.startsWith("gid://")) return id;
      return `gid://shopify/ProductVariant/${id}`;
    }

    function parseInventoryMap(text) {
      if (!text || !String(text).trim()) return null;
      try {
        const parsed = JSON.parse(text);
        return parsed && typeof parsed === "object" ? parsed : null;
      } catch (err) {
        console.warn("[location-inventory] invalid map JSON", err);
        return null;
      }
    }

    function readMapFromElement(el) {
      if (!el) return null;
      if (mapCache.has(el)) return mapCache.get(el);
      const map = parseInventoryMap(el.textContent);
      mapCache.set(el, map);
      return map;
    }

    function findMapElement(contextEl) {
      if (!contextEl) {
        return document.querySelector(".location-inventory-map");
      }
      const wrapper =
        contextEl.closest?.(".product-card__wrapper") ||
        contextEl.closest?.(".product-form") ||
        contextEl.closest?.("[data-section-id]") ||
        contextEl;
      return (
        wrapper?.querySelector?.(".location-inventory-map") ||
        contextEl.querySelector?.(".location-inventory-map") ||
        document.querySelector(".location-inventory-map")
      );
    }

    /**
     * Match a store name to a locations map entry.
     * locations[name] may be boolean (Liquid) or { available, quantityAvailable }.
     */
    function findLocationEntry(locations, storeName) {
      if (!locations || typeof locations !== "object" || !storeName) return null;
      const keys = Object.keys(locations);
      if (!keys.length) return null;

      const wrap = (key) => {
        const raw = locations[key];
        if (raw && typeof raw === "object" && ("available" in raw || "quantityAvailable" in raw)) {
          const qty = Number(raw.quantityAvailable);
          return {
            locationName: key,
            available: raw.available === true || (Number.isFinite(qty) && qty > 0),
            quantityAvailable: Number.isFinite(qty) ? qty : raw.available === true ? null : 0,
            source: raw.source || "unknown",
          };
        }
        // Liquid boolean fallback
        return {
          locationName: key,
          available: raw === true,
          quantityAvailable: raw === true ? null : 0,
          source: "liquid-boolean",
        };
      };

      if (Object.prototype.hasOwnProperty.call(locations, storeName)) {
        return wrap(storeName);
      }

      const storeNorm = normalizeName(storeName);
      for (const key of keys) {
        if (normalizeName(key) === storeNorm) return wrap(key);
      }

      for (const key of keys) {
        const keyNorm = normalizeName(key);
        if (
          (storeNorm.length >= 4 && keyNorm.includes(storeNorm)) ||
          (keyNorm.length >= 4 && storeNorm.includes(keyNorm))
        ) {
          return wrap(key);
        }
      }

      const storeMarket = marketKey(storeName);
      if (storeMarket) {
        for (const key of keys) {
          if (marketKey(key) === storeMarket) return wrap(key);
        }
      }

      return null;
    }

    /** Boolean helper for Alpine pickup UI / legacy callers */
    function lookupAvailability(availByLocation, storeName) {
      if (!availByLocation || typeof availByLocation !== "object") return null;
      if (!Object.keys(availByLocation).length) return null;
      const entry = findLocationEntry(availByLocation, storeName);
      if (!entry) {
        console.log("[location-inventory] selected store not found in availability map → OOS", {
          storeName,
          locations: Object.keys(availByLocation),
        });
        return false;
      }
      return entry.available === true;
    }

    function getVariantAvailabilityFromMap(map, variantId) {
      if (!map || variantId == null) return null;
      return map[String(variantId)] || map[variantId] || null;
    }

    function isVariantAvailableAtStore(variantId, storeName, contextEl) {
      if (!storeName || variantId == null) return null;
      const mapEl = findMapElement(contextEl);
      const map = readMapFromElement(mapEl);
      const availByLocation = getVariantAvailabilityFromMap(map, variantId);
      if (!availByLocation) return null;
      return lookupAvailability(availByLocation, storeName);
    }

    function buildInventoryResult(locations) {
      if (!locations || !Object.keys(locations).length) return null;
      let online = 0;
      Object.values(locations).forEach((row) => {
        const qty = Number(row?.quantityAvailable);
        if (Number.isFinite(qty) && qty > 0) online += qty;
      });
      return {
        locations,
        quantityAvailableOnline: online,
        availableForSale: online > 0,
        source: "metafield",
      };
    }

    function canCheckInventory() {
      return Boolean(getSelectedStore());
    }

    async function fetchVariantInventory(variantId, contextEl) {
      const id = String(variantId);
      const cacheKey = id;
      if (variantFetchCache.has(cacheKey)) return variantFetchCache.get(cacheKey);

      const promise = (async () => {
        const fromMap = inventoryFromMaps(id, contextEl);
        const locations = fromMap || inventoryFromDataAttrs(id, contextEl);
        const inv = buildInventoryResult(locations);
        console.log("[location-inventory] metafield inventory", {
          variantId: id,
          locations: inv?.locations,
          quantityAvailableOnline: inv?.quantityAvailableOnline,
        });
        return inv;
      })().catch((err) => {
        variantFetchCache.delete(cacheKey);
        console.error("[location-inventory] metafield inventory read failed", id, err);
        return null;
      });

      variantFetchCache.set(cacheKey, promise);
      return promise;
    }

    /** @deprecated boolean-only shape for older callers */
    async function fetchVariantAvailability(variantId, contextEl) {
      const inv = await fetchVariantInventory(variantId, contextEl);
      if (!inv?.locations) return null;
      const boolMap = {};
      Object.keys(inv.locations).forEach((name) => {
        boolMap[name] = inv.locations[name].available === true;
      });
      return boolMap;
    }

    /**
     * @returns {Promise<{ available: boolean, quantityAvailable: number|null, locationName: string|null, source: string|null, reason?: string }>}
     */
    async function resolveLocationStock(variantId, storeName, contextEl) {
      const store = String(storeName || getSelectedStore() || "").trim();

      if (!store || variantId == null) {
        return {
          available: false,
          quantityAvailable: 0,
          locationName: null,
          source: null,
          reason: store ? "missing-variant" : "missing-store",
        };
      }

      const inv = await fetchVariantInventory(variantId, contextEl);
      if (!inv) {
        return {
          available: false,
          quantityAvailable: 0,
          locationName: null,
          source: null,
          reason: "metafield-missing",
        };
      }

      if (!inv.locations || !Object.keys(inv.locations).length) {
        console.warn("[location-inventory] metafields returned no location rows → OOS", {
          variantId,
          store,
        });
        return {
          available: false,
          quantityAvailable: 0,
          locationName: null,
          source: inv.source,
          reason: "no-location-rows",
        };
      }

      const entry = findLocationEntry(inv.locations, store);
      if (!entry) {
        console.log("[location-inventory] store not in metafield locations → OOS", {
          variantId,
          store,
          locations: Object.keys(inv.locations),
        });
        return {
          available: false,
          quantityAvailable: 0,
          locationName: null,
          source: inv.source,
          reason: "store-not-matched",
        };
      }

      const qty = entry.quantityAvailable;
      const inStock =
        entry.available === true && (qty == null || (Number.isFinite(qty) && qty > 0));

      console.log("[location-inventory] location stock", {
        variantId,
        store,
        matchedLocation: entry.locationName,
        available: inStock,
        quantityAvailable: qty,
        source: entry.source || inv.source,
      });

      return {
        available: inStock,
        quantityAvailable: qty,
        locationName: entry.locationName,
        source: entry.source || inv.source,
      };
    }

    async function resolveVariantAvailability(variantId, storeName, contextEl) {
      const stock = await resolveLocationStock(variantId, storeName, contextEl);
      return stock.available === true;
    }

    function quantityExceededMessage(quantityAvailable, store) {
      const template =
        window.Maximize?.locationInventoryStrings?.quantityExceeded ||
        "Only {{ count }} available at {{ store }}.";
      return template
        .replace("{{ count }}", String(quantityAvailable ?? 0))
        .replace("{{ store }}", store || getSelectedStore() || "this location");
    }

    function unavailableMessage(store) {
      const prefix =
        window.Maximize?.locationInventoryStrings?.outOfStockAt || "Out of stock at ";
      const loc = store || getSelectedStore();
      return loc ? `${prefix}${loc}` : prefix.trim();
    }

    async function fetchCart() {
      const res = await fetch((window.Shopify?.routes?.root || "/") + "cart.js", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`cart.js failed: ${res.status}`);
      return res.json();
    }

    async function getCartQuantityForVariant(variantId) {
      try {
        const cart = await fetchCart();
        const id = Number(variantId);
        return (cart?.items || [])
          .filter((item) => Number(item.variant_id) === id)
          .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      } catch (err) {
        console.warn("[location-inventory] could not read cart qty", err);
        return 0;
      }
    }

    /**
     * Validate a desired total quantity for a variant at the selected store.
     * @returns {Promise<{ ok: boolean, message?: string, quantityAvailable?: number|null, stock?: object }>}
     */
    async function validateDesiredQuantity(variantId, desiredQty, contextEl) {
      const store = getSelectedStore();
      if (!canCheckInventory()) {
        console.warn("[location-inventory] block — inventory check unavailable", {
          store,
        });
        return {
          ok: false,
          message:
            window.Maximize?.locationInventoryStrings?.checkUnavailable ||
            "Location inventory could not be verified.",
          quantityAvailable: 0,
        };
      }

      const stock = await resolveLocationStock(variantId, store, contextEl);
      const desired = Math.max(0, Number(desiredQty) || 0);

      if (stock.available !== true) {
        console.warn("[location-inventory] block — not available at store", {
          variantId,
          store,
          desired,
          stock,
        });
        return {
          ok: false,
          message: unavailableMessage(store),
          quantityAvailable: stock.quantityAvailable ?? 0,
          stock,
        };
      }

      if (
        stock.quantityAvailable != null &&
        Number.isFinite(stock.quantityAvailable) &&
        desired > stock.quantityAvailable
      ) {
        console.warn("[location-inventory] block — qty exceeds location stock", {
          variantId,
          store,
          desired,
          quantityAvailable: stock.quantityAvailable,
        });
        return {
          ok: false,
          message: quantityExceededMessage(stock.quantityAvailable, store),
          quantityAvailable: stock.quantityAvailable,
          stock,
        };
      }

      return { ok: true, quantityAvailable: stock.quantityAvailable, stock };
    }

    /**
     * Before cart/add: cart qty + requested must be ≤ location stock.
     * @returns {Promise<{ ok: boolean, message?: string, quantityAvailable?: number|null }>}
     */
    async function ensureCanAddVariant(variantId, contextEl, requestedQty = 1) {
      const inCart = await getCartQuantityForVariant(variantId);
      const desired = inCart + (Number(requestedQty) || 1);
      console.log("[location-inventory] ensureCanAddVariant", {
        variantId,
        inCart,
        requestedQty,
        desired,
      });
      return validateDesiredQuantity(variantId, desired, contextEl);
    }

    /**
     * Before cart line qty change (plus / typed qty).
     */
    async function ensureCanSetLineQuantity(variantId, desiredQty, contextEl) {
      console.log("[location-inventory] ensureCanSetLineQuantity", {
        variantId,
        desiredQty,
      });
      return validateDesiredQuantity(variantId, desiredQty, contextEl);
    }

    function isProductFormData(data) {
      return Boolean(
        data &&
          "errorMessage" in data &&
          ("isAvailableButton" in data || data.$refs?.error_message)
      );
    }

    function findProductFormAlpineData(contextEl) {
      if (!window.Alpine?.$data) return null;

      let el = contextEl?.closest?.("[x-data]") || contextEl;
      while (el) {
        try {
          const data = Alpine.$data(el);
          if (isProductFormData(data)) return data;
        } catch (_) {}
        el = el.parentElement?.closest?.("[x-data]") || null;
      }

      // Quantity can live outside the buy-buttons Alpine scope — search the product section.
      const section =
        contextEl?.closest?.(".product-info") ||
        contextEl?.closest?.("[id^='ProductInfo']") ||
        contextEl?.closest?.(".shopify-section") ||
        document;
      const candidates = section.querySelectorAll?.(
        ".product-info__form-wrapper[x-data], .block-buy-buttons[x-data], [x-data*='xProductForm']"
      );
      for (const candidate of candidates || []) {
        try {
          const data = Alpine.$data(candidate);
          if (isProductFormData(data)) return data;
        } catch (_) {}
      }
      return null;
    }

    /** Show inline PDP/card flash error (theme UI customers already see on ATC failures). */
    function showInventoryErrorOnForm(message, contextEl) {
      const text = message || "Not enough inventory at this location.";
      const formData = findProductFormAlpineData(contextEl);
      if (formData) {
        const errorEl = formData.$refs?.error_message;
        if (errorEl) {
          errorEl.textContent = text;
          const parent = errorEl.parentElement;
          if (parent) {
            parent.classList.remove("hidden");
            parent.classList.add("flex");
          }
        }
        formData.errorMessage = true;
        window.clearTimeout(formData._locationInventoryErrorTimer);
        formData._locationInventoryErrorTimer = window.setTimeout(() => {
          formData.errorMessage = false;
          if (errorEl?.parentElement) {
            errorEl.parentElement.classList.add("hidden");
            errorEl.parentElement.classList.remove("flex");
          }
        }, 5000);
        return true;
      }

      const scope =
        contextEl?.closest?.(".product-info") ||
        contextEl?.closest?.(".product-card__wrapper") ||
        contextEl?.closest?.("[data-section-id]") ||
        document;
      const errorEl =
        scope.querySelector?.('[x-ref="error_message"]') ||
        scope.querySelector?.(".flash-message.error-message");
      if (!errorEl) return false;

      errorEl.textContent = text;
      const parent = errorEl.parentElement;
      if (parent) {
        parent.classList.remove("hidden");
        parent.classList.add("flex");
        window.clearTimeout(parent._locationInventoryErrorTimer);
        parent._locationInventoryErrorTimer = window.setTimeout(() => {
          parent.classList.add("hidden");
          parent.classList.remove("flex");
        }, 5000);
      }
      return true;
    }

    function showInventoryErrorToast(message, contextEl) {
      const text = message || "Not enough inventory at this location.";
      showInventoryErrorOnForm(text, contextEl);

      const existing = document.getElementById("LocationInventoryToast");
      if (existing) existing.remove();

      const toast = document.createElement("div");
      toast.id = "LocationInventoryToast";
      toast.setAttribute("role", "alert");
      toast.setAttribute("aria-live", "assertive");
      toast.style.cssText = [
        "position:fixed",
        "z-index:2147483646",
        "left:50%",
        "bottom:max(1.5rem, env(safe-area-inset-bottom, 0px))",
        "transform:translateX(-50%)",
        "max-width:min(28rem,calc(100% - 2rem))",
        "padding:0.9rem 1.15rem",
        "background:#b42318",
        "color:#fff",
        "border-radius:0.5rem",
        "box-shadow:0 10px 28px rgba(0,0,0,0.28)",
        "font-size:0.95rem",
        "line-height:1.35",
        "text-align:center",
        "pointer-events:none",
        "font-weight:600",
      ].join(";");
      toast.textContent = text;
      document.body.appendChild(toast);
      window.setTimeout(() => toast.remove(), 5000);
    }

    /**
     * Validate cart lines against location qty.
     * Returns unavailable (remove) and adjusted (reduce qty) lists.
     */
    async function validateCartForStore(storeName) {
      const store = String(storeName || getSelectedStore() || "").trim();
      if (!store) {
        console.error("[location-inventory] cart validation skipped — store required", { store });
        return { store, unavailable: [], adjusted: [], kept: [], checked: false };
      }
      // Metafield maps may have been replaced by a cart section re-render.
      variantFetchCache.clear();

      const cart = await fetchCart();
      const items = Array.isArray(cart?.items) ? cart.items : [];
      if (!items.length) {
        return { store, unavailable: [], adjusted: [], kept: [], checked: true, cart };
      }

      const uniqueVariantIds = [...new Set(items.map((i) => i.variant_id).filter(Boolean))];
      const invByVariant = {};
      await Promise.all(
        uniqueVariantIds.map(async (variantId) => {
          invByVariant[variantId] = await fetchVariantInventory(variantId);
        })
      );

      const unavailable = [];
      const adjusted = [];
      const kept = [];
      /** @type {Record<string, number>} */
      const quantityUpdates = {};

      for (const item of items) {
        const inv = invByVariant[item.variant_id];
        // Can't verify → remove (inventory check is required)
        if (!inv?.locations || !Object.keys(inv.locations).length) {
          unavailable.push({
            key: item.key,
            variant_id: item.variant_id,
            product_title: item.product_title,
            variant_title: item.variant_title,
            quantity: item.quantity,
            title: item.title,
            quantityAvailable: 0,
            action: "remove",
          });
          quantityUpdates[item.key] = 0;
          continue;
        }

        const entry = findLocationEntry(inv.locations, store);
        const qty = entry?.quantityAvailable;
        const inStock =
          entry &&
          entry.available === true &&
          (qty == null || (Number.isFinite(qty) && qty > 0));

        if (!inStock) {
          unavailable.push({
            key: item.key,
            variant_id: item.variant_id,
            product_title: item.product_title,
            variant_title: item.variant_title,
            quantity: item.quantity,
            title: item.title,
            quantityAvailable: entry?.quantityAvailable ?? 0,
            action: "remove",
          });
          quantityUpdates[item.key] = 0;
          continue;
        }

        if (
          entry.quantityAvailable != null &&
          Number.isFinite(entry.quantityAvailable) &&
          item.quantity > entry.quantityAvailable
        ) {
          adjusted.push({
            key: item.key,
            variant_id: item.variant_id,
            product_title: item.product_title,
            variant_title: item.variant_title,
            quantity: item.quantity,
            title: item.title,
            quantityAvailable: entry.quantityAvailable,
            action: "reduce",
          });
          quantityUpdates[item.key] = entry.quantityAvailable;
          continue;
        }

        kept.push(item);
      }

      console.log("[location-inventory] cart validated for store", {
        store,
        unavailableCount: unavailable.length,
        adjustedCount: adjusted.length,
        keptCount: kept.length,
        unavailable,
        adjusted,
      });

      return {
        store,
        unavailable,
        adjusted,
        kept,
        quantityUpdates,
        checked: true,
        cart,
      };
    }

    async function applyCartQuantityUpdates(quantityUpdates) {
      const keys = Object.keys(quantityUpdates || {});
      if (!keys.length) return null;

      selfUpdatingCart = true;
      try {
        const res = await fetch((window.Shopify?.routes?.root || "/") + "cart/update.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({ updates: quantityUpdates }),
        });
        if (!res.ok) throw new Error(`cart/update.js failed: ${res.status}`);
        const updated = await res.json();

        document.dispatchEvent(
          new CustomEvent(EVENT_CART_UPDATED, {
            bubbles: true,
            detail: {
              reason: "location-inventory",
              cart: updated,
              openMiniCart: false,
              isItemCountChanged: true,
            },
          })
        );

        return updated;
      } finally {
        selfUpdatingCart = false;
      }
    }

    async function removeCartLines(unavailableItems) {
      if (!unavailableItems?.length) return null;
      const updates = {};
      unavailableItems.forEach((item) => {
        if (item.key) updates[item.key] = 0;
      });
      return applyCartQuantityUpdates(updates);
    }

    function strings() {
      return (
        window.Maximize?.locationInventoryStrings || {
          title: "Inventory check",
          body: "Some items in your cart aren’t available at the new location and were updated:",
          cartBody:
            "Some items aren’t available in the quantity you selected at this location and were updated:",
          confirm: "Got it",
          emptyBody: "We checked your cart against this location’s inventory.",
          outOfStockAt: "Out of stock at",
        }
      );
    }

    function showInventoryNotice({ store, unavailable = [], adjusted = [], reason = "location-change" }) {
      return new Promise((resolve) => {
        const s = strings();
        const existing = document.getElementById("LocationInventoryNotice");
        if (existing) existing.remove();

        const changed = [...unavailable, ...adjusted];
        const overlay = document.createElement("div");
        overlay.id = "LocationInventoryNotice";
        overlay.setAttribute("role", "dialog");
        overlay.setAttribute("aria-modal", "true");
        overlay.style.cssText =
          "position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;padding:1.25rem;background:rgba(0,0,0,0.45);";

        const itemsHtml = changed.length
          ? `<ul style="text-align:left;margin:0 0 1.25rem;padding-left:1.25rem;max-height:12rem;overflow:auto;">
              ${changed
                .map((item) => {
                  const detail =
                    item.action === "reduce"
                      ? `qty ${item.quantity} → ${item.quantityAvailable}`
                      : `removed (had × ${item.quantity})`;
                  return `<li style="margin-bottom:0.35rem;">${escapeHtml(item.title || item.product_title)} <span style="opacity:0.7;">${escapeHtml(detail)}</span></li>`;
                })
                .join("")}
            </ul>`
          : "";

        const bodyText = changed.length
          ? reason === "cart-update"
            ? s.cartBody || s.body
            : s.body
          : s.emptyBody;

        overlay.innerHTML = `
          <div style="width:100%;max-width:26rem;padding:1.5rem 1.5rem 1.25rem;background:rgb(var(--color-background, 255 255 255));color:rgb(var(--color-foreground, 0 0 0));box-shadow:0 12px 40px rgba(0,0,0,0.18);border-radius:var(--border-radius, 0.5rem);text-align:center;">
            <p class="h4" style="margin:0 0 0.75rem;">${escapeHtml(s.title)}</p>
            <p style="margin:0 0 1rem;opacity:0.85;font-size:0.95rem;line-height:1.4;">${escapeHtml(bodyText)}</p>
            ${store ? `<p style="margin:0 0 1rem;font-weight:600;">${escapeHtml(store)}</p>` : ""}
            ${itemsHtml}
            <button type="button" data-location-inventory-confirm class="button button-primary" style="width:100%;font-weight:600;">
              ${escapeHtml(s.confirm)}
            </button>
          </div>
        `;

        const close = () => {
          overlay.remove();
          resolve();
        };
        overlay.addEventListener("click", (e) => {
          if (e.target === overlay) close();
        });
        overlay.querySelector("[data-location-inventory-confirm]")?.addEventListener("click", close);
        document.body.appendChild(overlay);
      });
    }

    function escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    async function applyValidationResult(result, reason) {
      const updates = result.quantityUpdates || {};
      const changed = [...(result.unavailable || []), ...(result.adjusted || [])];
      if (!changed.length) {
        document.dispatchEvent(
          new CustomEvent(EVENT_INVENTORY_CHECKED, {
            detail: { store: result.store, removed: [], adjusted: [], kept: result.kept, reason },
          })
        );
        return { removed: [], adjusted: [], kept: result.kept, skipped: false };
      }

      await applyCartQuantityUpdates(updates);
      await showInventoryNotice({
        store: result.store,
        unavailable: result.unavailable,
        adjusted: result.adjusted,
        reason,
      });

      document.dispatchEvent(
        new CustomEvent(EVENT_INVENTORY_CHECKED, {
          detail: {
            store: result.store,
            removed: result.unavailable,
            adjusted: result.adjusted,
            kept: result.kept,
            reason,
          },
        })
      );

      return {
        removed: result.unavailable,
        adjusted: result.adjusted,
        kept: result.kept,
        skipped: false,
      };
    }

    async function reconcileCartForStoreChange(storeName) {
      const store = String(storeName || "").trim();
      if (!store) return { removed: [], adjusted: [], skipped: true };

      try {
        const result = await validateCartForStore(store);
        return await applyValidationResult(result, "location-change");
      } catch (err) {
        console.error("[location-inventory] reconcileCartForStoreChange failed", err);
        return { removed: [], adjusted: [], error: err, skipped: false };
      }
    }

    async function reconcileCartAfterUpdate(reason = "cart-update") {
      const store = getSelectedStore();
      if (!store) return { removed: [], adjusted: [], skipped: true };
      if (selfUpdatingCart) return { removed: [], adjusted: [], skipped: true };
      if (cartCheckPromise) return cartCheckPromise;

      cartCheckPromise = (async () => {
        try {
          console.log("[location-inventory] checking cart after update", { store, reason });
          const result = await validateCartForStore(store);
          return await applyValidationResult(result, "cart-update");
        } catch (err) {
          console.error("[location-inventory] reconcileCartAfterUpdate failed", err);
          return { removed: [], adjusted: [], error: err, skipped: false };
        } finally {
          cartCheckPromise = null;
        }
      })();

      return cartCheckPromise;
    }

    function outOfStockLabel(storeName) {
      const prefix =
        window.Maximize?.locationInventoryStrings?.outOfStockAt ||
        window.Maximize?.variantStrings?.soldOut ||
        "Out of stock";
      const store = storeName || getSelectedStore();
      // Prefix strings from locales already include a trailing space ("Out of stock at ").
      const normalizedPrefix = String(prefix).replace(/\s+$/, "");
      return store ? `${normalizedPrefix} ${store}` : normalizedPrefix;
    }

    /**
     * Rewrite PDP / card "Low in stock" labels to match selected-location qty
     * (Liquid uses total shop inventory, which disagrees with per-store ATC).
     */
    function applyStockToInventoryStatusEl(el, stock) {
      if (!el || el.dataset.isPreorder === "true") return;

      const threshold = Number(el.dataset.lowStockThreshold);
      const lowThreshold = Number.isFinite(threshold) && threshold > 0 ? threshold : 4;
      const qty =
        stock?.quantityAvailable != null && Number.isFinite(Number(stock.quantityAvailable))
          ? Number(stock.quantityAvailable)
          : null;
      const available = stock?.available === true && (qty == null || qty > 0);

      let status;
      let text;
      if (!available) {
        status = "out-of-stock";
        text = el.dataset.labelOutOfStock || "Out of stock";
      } else if (qty != null && qty <= lowThreshold) {
        status = "low-stock";
        text = (el.dataset.labelLowStock || "Low in stock").replace(/\{qty\}/gi, String(qty));
      } else {
        status = "in-stock";
        text = (el.dataset.labelInStock || "In stock").replace(
          /\{qty\}/gi,
          qty != null ? String(qty) : ""
        );
      }

      el.setAttribute("data-inventory-status", status);
      el.dataset.inventoryStatus = status;
      el.querySelectorAll("[data-inventory-status]").forEach((node) => {
        node.setAttribute("data-inventory-status", status);
      });
      const textEl = el.querySelector("[data-inventory-status-text]");
      if (textEl) textEl.textContent = text;

      applyLocationCardChrome(el, available);

      console.log("[location-inventory] inventory status label updated", {
        variantId: el.dataset.variantId,
        store: getSelectedStore(),
        status,
        quantityAvailable: qty,
        text,
      });
    }

    /**
     * Product cards in promotional / featured carousels also show a Sold out
     * badge and Choose option CTA from shop-wide availability. Keep those in
     * sync with the selected store.
     */
    function applyLocationCardChrome(statusEl, available) {
      const card = statusEl?.closest?.(".product-card__wrapper");
      if (!card) return;
      applyCardSoldOutBadge(card, available);
      applyCardChooseOptionCta(card, available);
    }

    function applyCardSoldOutBadge(card, available) {
      const labelEl = card.querySelector("[x-labels-data]");
      if (!labelEl) return;

      const parseLabelsData = (raw) => {
        if (!raw) return null;
        try {
          return JSON.parse(raw);
        } catch (_) {}
        try {
          return (window.xParseJSON || JSON.parse)(raw);
        } catch (_) {
          return null;
        }
      };

      const data = parseLabelsData(labelEl.getAttribute("x-labels-data"));
      if (!data || typeof data !== "object") return;
      if (data.available === available) return;

      data.available = available;
      labelEl.setAttribute("x-labels-data", JSON.stringify(data));
      labelEl.querySelectorAll(".label-container, .x-badge-container").forEach((node) => node.remove());

      const loader = window.Alpine?.store?.("xLabelsAndBadges")?.load;
      if (typeof loader === "function") loader(labelEl);
    }

    function applyCardChooseOptionCta(card, available) {
      card.querySelectorAll("[data-location-card-cta='choose-option']").forEach((btn) => {
        const textEl = btn.querySelector(".btn__text");
        const inStockLabel = btn.dataset.labelChooseOption || "Choose option";
        const soldOutLabel =
          btn.dataset.labelSoldOut ||
          window.Maximize?.variantStrings?.soldOut ||
          "Sold out";
        btn.disabled = !available;
        btn.setAttribute("aria-disabled", available ? "false" : "true");
        btn.classList.toggle("opacity-60", !available);
        btn.classList.toggle("cursor-not-allowed", !available);
        btn.classList.toggle("pointer-events-none", !available);
        if (textEl) textEl.textContent = available ? inStockLabel : soldOutLabel;
      });
    }

    async function refreshInventoryStatusDisplays(root = document) {
      variantFetchCache.clear();
      if (!getSelectedStore()) return;

      const scope = root?.querySelectorAll ? root : document;
      const els = [...scope.querySelectorAll("[data-location-inventory-status]")];
      if (!els.length) return;

      const byVariant = new Map();
      els.forEach((el) => {
        const id = String(el.dataset.variantId || "").trim();
        if (!id || el.dataset.isPreorder === "true") return;
        if (!byVariant.has(id)) byVariant.set(id, []);
        byVariant.get(id).push(el);
      });

      const store = getSelectedStore();
      await Promise.all(
        [...byVariant.entries()].map(async ([variantId, group]) => {
          const stock = await resolveLocationStock(variantId, store, group[0]);
          group.forEach((el) => applyStockToInventoryStatusEl(el, stock));
        })
      );
    }

    function applyLocationInventoryStatus(variantId, stock, contextEl) {
      const scope =
        contextEl?.closest?.(".shopify-section") ||
        contextEl?.closest?.(".product-info") ||
        contextEl?.closest?.(".product-card__wrapper") ||
        document;
      const id = String(variantId || "").trim();
      if (!id) {
        refreshInventoryStatusDisplays(scope);
        return;
      }
      scope
        .querySelectorAll(`[data-location-inventory-status][data-variant-id="${id}"]`)
        .forEach((el) => applyStockToInventoryStatusEl(el, stock));
    }

    document.addEventListener(EVENT_STORE_CHANGED, () => {
      // Invalidate inventory cache when store changes so qty is re-fetched
      variantFetchCache.clear();
      document.dispatchEvent(new CustomEvent("maximize:location-inventory:refresh-atc"));
      refreshInventoryStatusDisplays();
    });

    document.addEventListener("maximize:location-inventory:refresh-atc", () => {
      refreshInventoryStatusDisplays();
    });

    document.addEventListener("maximize:catalog:ready", () => {
      refreshInventoryStatusDisplays();
    });

    document.addEventListener(EVENT_CART_UPDATED, (e) => {
      if (selfUpdatingCart || e?.detail?.reason === "location-inventory") return;
      if (e?.detail?.reason === "active-location" && !e?.detail?.isItemCountChanged) return;
      reconcileCartAfterUpdate(e?.detail?.reason || "cart-update");
    });

    function debounce(fn, wait) {
      let t;
      return function () {
        clearTimeout(t);
        t = setTimeout(fn, wait);
      };
    }

    const refreshInventoryStatusSoon = debounce(() => refreshInventoryStatusDisplays(), 100);

    function observeInventoryStatusNodes() {
      if (!document.body || typeof MutationObserver === "undefined") return;
      const mo = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (
              node.matches?.("[data-location-inventory-status]") ||
              node.querySelector?.("[data-location-inventory-status]")
            ) {
              refreshInventoryStatusSoon();
              return;
            }
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    // Initial paint: Liquid may show total-shop "In stock" until this runs.
    function initInventoryStatusDisplays() {
      refreshInventoryStatusDisplays();
      observeInventoryStatusNodes();
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initInventoryStatusDisplays);
    } else {
      initInventoryStatusDisplays();
    }

    window.Maximize = window.Maximize || {};
    window.Maximize.locationInventory = {
      getSelectedStore,
      isVariantAvailableAtStore,
      resolveVariantAvailability,
      resolveLocationStock,
      validateDesiredQuantity,
      ensureCanAddVariant,
      ensureCanSetLineQuantity,
      getCartQuantityForVariant,
      showInventoryErrorToast,
      showInventoryErrorOnForm,
      lookupAvailability,
      findLocationEntry,
      fetchVariantAvailability,
      fetchVariantInventory,
      validateCartForStore,
      reconcileCartForStoreChange,
      reconcileCartAfterUpdate,
      outOfStockLabel,
      applyLocationInventoryStatus,
      refreshInventoryStatusDisplays,
      findMapElement,
      readMapFromElement,
      hasStorefrontToken: () => false,
      hasMetafieldInventory: () => true,
      canCheckInventory,
    };
  })();
}
